import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { getLocalApiUrl, authFetch } from '../config/api'
import { resolveFlagEnabled } from '../utils/permissionFlagResolver'
import { parseIsClub, patronLabels } from '../utils/tenantClubMode'

const FeatureFlagContext = createContext({})
const IS_CLUB_STORAGE_KEY = 'paysafego_is_club'

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext)
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider')
  }
  return context
}

/**
 * Check if a specific flag is enabled.
 * Convenience hook for single flag checks.
 */
export const useFeatureFlag = (flagKey) => {
  const { isEnabled } = useFeatureFlags()
  return isEnabled(flagKey)
}

/** Club vs non-club display labels for the current tenant (Visitor ↔ Non-member). */
export const usePatronLabels = () => {
  const { isClub } = useFeatureFlags()
  return useMemo(() => patronLabels(isClub), [isClub])
}

export const FeatureFlagProvider = ({ children }) => {
  const [flags, setFlags] = useState({})
  const [isClub, setIsClub] = useState(true)
  const [loading, setLoading] = useState(true)

  const persistIsClub = useCallback((value) => {
    const club = parseIsClub(value, true)
    setIsClub(club)
    localStorage.setItem(IS_CLUB_STORAGE_KEY, club ? '1' : '0')
  }, [])

  // Load flags from localStorage on mount (cached from login)
  useEffect(() => {
    try {
      const stored = localStorage.getItem('paysafego_feature_flags')
      if (stored) {
        setFlags(JSON.parse(stored))
      }
      const storedClub = localStorage.getItem(IS_CLUB_STORAGE_KEY)
      if (storedClub != null) {
        setIsClub(parseIsClub(storedClub === '1' || storedClub === 'true', true))
      }
    } catch (_) { /* ignore parse errors */ }
    setLoading(false)
  }, [])

  /**
   * Set flags from login response data.
   * Called by AuthContext after successful login.
   * @param {object} featureFlags
   * @param {boolean} [isClubFromLogin]
   */
  const setFlagsFromLogin = useCallback((featureFlags, isClubFromLogin) => {
    const flagMap = featureFlags || {}
    setFlags(flagMap)
    localStorage.setItem('paysafego_feature_flags', JSON.stringify(flagMap))
    if (isClubFromLogin !== undefined) {
      persistIsClub(isClubFromLogin)
    }
  }, [persistIsClub])

  /**
   * Refresh flags from the API (e.g., after admin changes).
   */
  const refreshFlags = useCallback(async () => {
    try {
      const res = await authFetch(getLocalApiUrl('/api/feature-flags'))
      if (res.ok) {
        const data = await res.json()
        if (data.success && data.flags) {
          setFlags(data.flags)
          localStorage.setItem('paysafego_feature_flags', JSON.stringify(data.flags))
        }
        if (data.success && typeof data.isClub === 'boolean') {
          persistIsClub(data.isClub)
        }
      }
    } catch (err) {
      console.warn('Failed to refresh feature flags:', err)
    }
  }, [persistIsClub])

  /** Prefer React state; fall back to login-cached localStorage (avoids race right after MFA login). */
  const getEffectiveFlags = useCallback(() => {
    if (flags && Object.keys(flags).length > 0) return flags
    try {
      const stored = localStorage.getItem('paysafego_feature_flags')
      if (stored) {
        const parsed = JSON.parse(stored)
        if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) return parsed
      }
    } catch (_) { /* ignore */ }
    return flags || {}
  }, [flags])

  /**
   * Check if a flag is enabled. Supports:
   * - Exact key: 'module:profile-safe'
   * - Page key: 'page:profile-safe/onboarding'
   * - Feature key: 'feature:aml-screening'
   *
   * Uses resolveFlagEnabled — same rules as the permissions matrix.
   */
  const isEnabled = useCallback((flagKey) => {
    return resolveFlagEnabled(getEffectiveFlags(), flagKey)
  }, [getEffectiveFlags])

  /**
   * Check if a module is enabled (convenience).
   */
  const isModuleEnabled = useCallback((moduleName) => {
    return isEnabled(`module:${moduleName}`)
  }, [isEnabled])

  /**
   * Check if a page route is enabled.
   * Checks both the page flag and its parent module flag.
   */
  const isPageEnabled = useCallback((routePath) => {
    const effectiveFlags = getEffectiveFlags()
    const flagEnabled = (key) => resolveFlagEnabled(effectiveFlags, key)

    // Legacy URL redirects to payout builder; use builder's flags (admin duplicate removed).
    const path = String(routePath || '').replace(/\/$/, '') || '/'
    const pathForFlags = path === '/admin/venue-payouts' ? '/payout-safe/builder' : path

    // Check module-level flag first
    // Extract module from route: '/profile-safe/onboarding' -> 'profile-safe'
    const parts = pathForFlags.replace(/^\//, '').split('/').filter(Boolean)
    const moduleName = parts[0]
    if (!moduleName) return true
    if (!flagEnabled(`module:${moduleName}`)) return false

    const isFormsProductPath =
      pathForFlags === '/admin/form-builder' ||
      pathForFlags.startsWith('/admin/form-builder/') ||
      pathForFlags === '/compliance-safe/forms' ||
      pathForFlags.startsWith('/compliance-safe/forms/')
    if (isFormsProductPath && !flagEnabled('module:forms')) return false

    // Prefer the most specific catalogued page flag, then ancestors.
    // /compliance-safe/forms/assignments/:id follows page:compliance-safe/forms
    // when no assignment-level flag exists.
    for (let i = parts.length; i >= 1; i -= 1) {
      const key = `page:${parts.slice(0, i).join('/')}`
      if (Object.prototype.hasOwnProperty.call(effectiveFlags, key)) {
        return effectiveFlags[key] === true
      }
    }
    return true
  }, [getEffectiveFlags])

  /**
   * Clear flags (on logout).
   */
  const clearFlags = useCallback(() => {
    setFlags({})
    setIsClub(true)
    localStorage.removeItem('paysafego_feature_flags')
    localStorage.removeItem(IS_CLUB_STORAGE_KEY)
  }, [])

  const value = {
    flags,
    isClub,
    loading,
    getEffectiveFlags,
    isEnabled,
    isModuleEnabled,
    isPageEnabled,
    setFlagsFromLogin,
    refreshFlags,
    clearFlags,
  }

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  )
}

export default FeatureFlagContext
